// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { createColumnHelper } from '@tanstack/react-table';
import { type AccountInfo } from '@shared/data-access/interfaces/api/brokerage/account';
import { Table, IconButton } from '@shared/ui-kit';
import { capitalizeString } from '@shared/ui-kit/utils/capitalize-string';
import { type FC, useMemo } from 'react';
import { useAppSelector } from '@advisor-app/store';
import { selectAccountCurrency } from '@shared/data-access/slices/user';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';

export interface AccountsTable {
  readonly data: AccountInfo[];
  readonly onSetAccount: (accountInfo: AccountInfo[]) => void;
  readonly broker: string;
  readonly accountId?: string;
}

const columnHelper = createColumnHelper<AccountInfo>();

export const AccountsTable: FC<AccountsTable> = ({
  data,
  broker,
  onSetAccount,
  accountId,
}) => {
  const { t } = useTranslation();
  const currency = useAppSelector(selectAccountCurrency);
  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: t('common.name', { ns: 'common' }),
        cell: info => {
          return (
            <div className="flex items-center gap-x-2">
              <span>{capitalizeString(info.getValue() || broker)}</span>
            </div>
          );
        },
        minSize: 200,
      }),
      columnHelper.accessor('account_manager', {
        header: t('accounts.account-manager', { ns: 'components' }),
        cell: info => <span>{capitalizeString(info.getValue() || broker)}</span>,
        minSize: 200,
      }),
      columnHelper.accessor('currency', {
        header: t('common.currency', { ns: 'common' }),
        cell: info => info.getValue() || '-',
        maxSize: 200,
      }),
      columnHelper.accessor('investment', {
        header: t('accounts.equity', { ns: 'components' }),
        meta: { alignment: 'end' },
        cell: info => (
          <Table.Data
            format="currency"
            currency={currency}
            value={info.getValue()}
          />
        ),
        maxSize: 200,
      }),
      columnHelper.accessor('aum', {
        header: t('common.investment', { ns: 'common' }),
        meta: { alignment: 'end' },
        cell: info => (
          <Table.Data
            format="currency"
            currency={currency}
            value={info.getValue()}
          />
        ),
        maxSize: 200,
      }),
      columnHelper.accessor('cash', {
        header: t('common.cash', { ns: 'common' }),
        meta: { alignment: 'end' },
        cell: info => (
          <Table.Data
            format="currency"
            currency={currency}
            value={info.getValue()}
          />
        ),
        maxSize: 200,
      }),
      columnHelper.accessor('unrealized_capital_gains', {
        header: t('common.gain-loss', { ns: 'common' }),
        meta: { alignment: 'end' },
        cell: info => (
          <Table.Data
            validate
            format="currency"
            currency={currency}
            value={info.getValue()}
          />
        ),
      }),
      columnHelper.display({
        header: t('common.actions', { ns: 'common' }),
        id: 'options',
        enableSorting: false,
        cell: ({ row }) => {
          const account = row.original;
          return (
            <div className="flex items-center justify-center gap-x-4">
              <Tooltip
                title={t('accounts.open-broker-dashboard-tooltip', {
                  ns: 'components',
                })}
                placement="top"
              >
                <span>
                  <IconButton
                    icon="Dashboard"
                    variant="icon"
                    size="lg"
                    onClick={e => {
                      e.stopPropagation();
                      onSetAccount([account]);
                    }}
                    data-testid={`open-dashboard-${account.account_id}`}
                  />
                </span>
              </Tooltip>
            </div>
          );
        },
      }),
    ],
    [broker, currency, onSetAccount, t],
  );

  const initialRowSelection = useMemo(
    () =>
      data.length > 0
        ? {
            [accountId ?? data[0].account_id]: true,
          }
        : {},
    [accountId, data],
  );

  return (
    <div className="overflow-x-auto">
      <Table.Grouped
        data={data}
        columns={columns}
        emptyState={
          broker
            ? t('accounts.no-account-data-reconnect', { ns: 'components' })
            : t('accounts.no-account-data-connect', { ns: 'components' })
        }
        onRowSelectionChange={onSetAccount}
        enableRowSelection
        getRowId={row => row.account_id}
        initialRowSelection={initialRowSelection}
        disableDeselect
      />
    </div>
  );
};
